import { assign } from 'lodash';
import { ChangeDetectionStrategy, Component, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup, FormControl } from '@angular/forms';
import * as FlatModels from '@medsurf/flat-models';
import * as FlatFacades from '@medsurf/flat-facades';
import { ZodSharedValidator } from '@medsurf/flat-validators';

@Component({
    selector: 'medsurf-slide-edit-shared-name-form',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './shared-name-form.component.html',
    styleUrls: ['./shared-name-form.component.scss'],
    standalone: false
})
export class SharedNameFormComponent {
  #isSubmitted = signal(false);
  #annotationLabel: FlatModels.AnnotationLabelEntityModels.AnnotationLabel;
  annotationLabelForm = new FormGroup(
    {
      text: new FormControl(''),
      align: new FormControl(''),
      pivot: new FormControl(''),
      color: new FormControl(''),
      fontSize: new FormControl(14),
      border: new FormControl(false),
      borderColor: new FormControl(''),
      borderWidth: new FormControl(1),
      description: new FormControl(''),
      hidden: new FormControl(false),
    },
    {
      validators: ZodSharedValidator.validate(
        FlatModels.AnnotationLabelEntityModels.AnnotationLabelSchema.pick({
          text: true,
          align: true,
          pivot: true,
          color: true,
          fontSize: true,
          border: true,
          borderColor: true,
          borderWidth: true,
          description: true,
          hidden: true,
          background: true,
        })
      ),
      updateOn: 'change'
    }
  );
  annotationLabelBackgroundForm = new FormGroup(
    {
      color: new FormControl(''),
      opacity: new FormControl(40),
    },
    {
      validators: ZodSharedValidator.validate(
        FlatModels.AnnotationLabelEntityModels.MarkerBackgroundSchema.pick({
          color: true,
          opacity: true,
        })
      ),
      updateOn: 'change'
    }
  );
  readonly FlatModels = FlatModels;

  constructor(public alertControlFacade: FlatFacades.AlertControlFacade,
              public slideAuthorFacade: FlatFacades.SlideAuthorFacade) {
    // Update Annotation Label
    this.slideAuthorFacade.currentSelectedSlideAnnotationLabel$.pipe(takeUntilDestroyed()).subscribe((annotationLabel) => {
      this.#annotationLabel = structuredClone(annotationLabel);
      this.annotationLabelForm.reset(annotationLabel);
      this.annotationLabelBackgroundForm.reset({
        color: annotationLabel?.background?.color,
        opacity: (annotationLabel?.background?.opacity || 0) * 100
      });
      this.#isSubmitted.set(false);
    });
  }

  onSubmit() {
    if (this.#isSubmitted() || this.annotationLabelForm.invalid || !this.#annotationLabel) {
      return;
    }
    this.#isSubmitted.set(true);
    
    // Update Annotation Label
    const annotationLabel = assign(
      this.#annotationLabel,
      this.annotationLabelForm.value,
      {
        background: {
          color: this.annotationLabelBackgroundForm.value.color,
          opacity: (this.annotationLabelBackgroundForm.value.opacity || 0) / 100,
        }
      }
    );
    this.slideAuthorFacade.requestUpdateAnnotationLabel(annotationLabel, 'history.page_update_description').subscribe({
      error: (result) => {
        ZodSharedValidator.handleError(result, this.alertControlFacade, this.annotationLabelForm);
        this.#isSubmitted.set(false);
      },
      next: () => {
        this.#isSubmitted.set(false);
      }
    });
  }
  
  onShowBackground() {
    if (this.annotationLabelBackgroundForm.value.opacity === 0) {
      this.annotationLabelBackgroundForm.patchValue({opacity: 40 });
    } else {
      this.annotationLabelBackgroundForm.patchValue({opacity: 0 });
    }
    if (!this.annotationLabelBackgroundForm.invalid) {
      this.onSubmit();
    }
  }
}
